/**
 * SectionVoicePanel.jsx
 *
 * Who reads the book's back matter — shown on the Rear Matter unit, and nowhere else.
 *
 * WHY IT LIVES HERE. A ToC-declared section (an appendix, an afterword) renders as its own unit,
 * one past the last chapter, with its own stitched file. Choosing its voice is a casting decision,
 * and the Studio is already the casting surface and already book-scoped — so the choice belongs
 * where you are standing when it matters, beside the section's own line-set. The Green Room was
 * considered and rejected: it is LitGroup-scoped, sections are per book, and exactly one book in a
 * 22-PDF corpus declares one, so the tab would have been permanently empty for everything else.
 *
 * NOTHING IS CREATED HERE. The picker offers characters that already exist, because the Narrator
 * is the only character the app guarantees — every other one is the book's own and ephemeral. A
 * section with no choice reads in the Narrator's voice, which is a valid answer rather than a
 * fault, and the panel says so plainly.
 *
 * The choice is applied at RENDER, not at ingest, so it takes effect on the next render instead of
 * waiting for a rebuilt script. See docs/journal/SECTION_NODE_AND_REAR_MATTER_VISIBILITY_SPEC.md.
 */

import React, { useEffect, useState } from "react";
import { Dropdown, Icon, Message, Segment } from "semantic-ui-react";
import { apiClient } from "../../apiTools/apiClient";

const NARRATOR = "__narrator__";

/** ~950 chars a minute is the working figure elsewhere in the pipeline. */
const minutesFor = (chars) => Math.max(1, Math.round((chars || 0) / 950));

const SectionVoicePanel = ({ bookId, sections }) => {
  const [characters, setCharacters] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // Local echo of what has been chosen, so a row updates without refetching the whole tree.
  const [chosen, setChosen] = useState(() =>
    Object.fromEntries((sections || []).map((s) => [s.key, s.voice || null]))
  );

  // Fetched lazily, only once this panel is actually on screen. Most books never declare a
  // section, and the Studio should not carry a roster it will not use.
  useEffect(() => {
    let cancelled = false;
    if (!bookId) return undefined;
    (async () => {
      try {
        const res = await apiClient.get(`/voice-rendering/${bookId}/characters`);
        if (cancelled) return;
        const list = res?.data?.characters || res?.data?.data?.characters || [];
        setCharacters(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.error?.message || err.message || "Could not load characters");
      }
    })();
    return () => { cancelled = true; };
  }, [bookId]);

  // NOT SILENCE. A script written before rear-matter members carried a `key` has nothing this
  // panel can address — projectRearMatterItem looks the choice up by key and there is none to
  // match — but rendering nothing leaves the operator selecting the unit, seeing no control, and
  // having to ask why. Say what is true instead: the book renders, the Narrator reads it, and a
  // re-import is what makes the choice available.
  if (!sections || !sections.length) {
    return (
      <Segment className="rud-section-voices" basic style={{ padding: "0 0 0 10px", marginTop: "1rem" }}>
        <Message size="tiny" info>
          <Icon name="microphone" />
          The Narrator reads this book&rsquo;s back matter. Choosing a different voice needs a
          re-ingest first — this script was built before sections carried one.
        </Message>
      </Segment>
    );
  }

  const options = [
    { key: NARRATOR, value: NARRATOR, text: "Narrator", icon: "microphone" },
    ...(characters || [])
      .filter((c) => c && c.uuid && c.name)
      .map((c) => ({ key: c.uuid, value: c.uuid, text: c.name })),
  ];

  const choose = async (section, value) => {
    const uuid = value === NARRATOR ? "" : value;
    setSaving(section.key);
    setSaveError(null);
    try {
      await apiClient.put(`/voice-rendering/${bookId}/section-voices`, {
        voices: { [section.key]: uuid },
      });
      const picked = (characters || []).find((c) => c.uuid === uuid);
      setChosen((prev) => ({ ...prev, [section.key]: picked ? picked.name : null }));
    } catch (err) {
      setSaveError(err?.response?.data?.error?.message || err.message || "Could not save");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Segment className="rud-section-voices" basic style={{ padding: "0 0 0 10px", marginTop: "1rem" }}>
      <div className="rud-subhead" style={{ fontWeight: 600, marginBottom: ".5rem" }}>
        Who reads this
      </div>

      {loadError && (
        <Message size="tiny" warning>
          <Icon name="warning sign" /> {loadError} — the picker is unavailable, so these sections
          will read in the Narrator&rsquo;s voice.
        </Message>
      )}
      {saveError && <Message size="tiny" negative><Icon name="warning sign" /> {saveError}</Message>}

      {sections.map((s, i) => {
        const current = chosen[s.key];
        const value = current
          ? (characters || []).find((c) => c.name === current)?.uuid || NARRATOR
          : NARRATOR;
        return (
          <div
            key={s.key}
            className="rud-section-voice-row"
            // An open Dropdown's menu is absolutely positioned inside its own row, so a LATER row
            // paints over it — the appendix's menu was drawn under the Closing Credits row. Giving
            // each row its own stacking context in descending order puts earlier rows on top,
            // which is the order their menus open downward into.
            style={{
              display: "flex", alignItems: "center", gap: ".75rem", marginBottom: ".5rem",
              position: "relative", zIndex: sections.length - i,
            }}
          >
            <div style={{ minWidth: "14rem" }}>
              <strong>{s.title}</strong>
              <div className="mono" style={{ opacity: 0.6, fontSize: ".85em" }}>
                {s.lines.toLocaleString()} lines · ~{minutesFor(s.chars)} min
                {s.speak === false ? " · not narrated" : ""}
              </div>
            </div>
            <Dropdown
              selection
              search
              disabled={!characters || saving === s.key}
              loading={saving === s.key}
              options={options}
              value={value}
              onChange={(_, d) => choose(s, d.value)}
              style={{ minWidth: "16rem" }}
            />
            {/*
              An ALL-CLEAR as well as a warning. A panel that only speaks up when something is
              wrong is indistinguishable from one that never ran — and "the Narrator will read it"
              is the answer for most books, not a problem to be fixed.
            */}
            <span style={{ opacity: 0.75, fontSize: ".9em" }}>
              {current
                ? <><Icon name="check" color="green" />{current} will read it.</>
                : <><Icon name="microphone" />No voice chosen — the Narrator will read it.</>}
            </span>
          </div>
        );
      })}

      <div style={{ opacity: 0.6, fontSize: ".85em", marginTop: ".5rem" }}>
        Applied on the next render — no re-ingest needed.
      </div>
    </Segment>
  );
};

export default SectionVoicePanel;
